import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Autocomplete,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { SERVER_URL } from "../../../constants";
import { can } from "../../shared utils/auth";
import { d10, maxStr, minStr, normCampus } from "../../shared utils/bookingsGrid.js";

const overlaps = (aS, aE, bS, bE) => Boolean(aS && aE && bS && bE) && aS <= bE && bS <= aE;
const sameCampus = (a, b) => !a || !b || normCampus(a) === normCampus(b);

// Normalise a stay (guard reversed arrival/departure) to { start, end, campus, raw, key }.
const normStay = (s, i) => {
  const a = d10(s.arrivalDate);
  const b = d10(s.departureDate);
  return {
    start: a && b ? minStr(a, b) : a || b,
    end: a && b ? maxStr(a, b) : a || b,
    campus: s.campus || "",
    raw: s,
    key: String(s.stayId ?? `idx-${i}`),
  };
};

const toBlock = (raw) => ({
  id: raw.bookingId,
  productId: raw.productId,
  productName: raw.productName,
  campus: raw.campus ?? raw.base ?? "",
  actualCharge: raw.actualCharge,
  start: d10(raw.startDate),
  end: d10(raw.endDate),
  raw,
});

const inYear = (year, a, b) => {
  if (!year) return true;
  const lo = minStr(d10(a), d10(b));
  const hi = maxStr(d10(a), d10(b));
  return lo <= `${year}-12-31` && `${year}-01-01` <= hi;
};

// Copy another student's setup onto this one: pick the source's stay(s) to duplicate, and
// for each, create a matching stay on the destination if it doesn't have one, then bring
// its bookings across (add / adjust / remove) so the destination matches. The whole plan
// is shown before anything is applied.
export default function StudentCopyBookingsDialog({
  open,
  onClose,
  token,
  selectedYear,
  destStudentId,
  destStays = [], // [{ start, end, campus }] — already normalised
  destBlocks = [], // normalised blocks already on the destination
  onApplied,
}) {
  const [students, setStudents] = useState([]);
  const [source, setSource] = useState(null);
  const [sourceStays, setSourceStays] = useState([]);
  const [sourceBlocks, setSourceBlocks] = useState([]);
  const [picked, setPicked] = useState(new Set()); // source stay keys to duplicate
  const [loadingSource, setLoadingSource] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSource(null);
    setSourceStays([]);
    setSourceBlocks([]);
    setPicked(new Set());
    fetch(`${SERVER_URL}api/studentsBasicYear/${selectedYear}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.status === 204 ? [] : r.ok ? r.json() : []))
      .then((data) => setStudents(Array.isArray(data) ? data : []))
      .catch(() => setStudents([]));
  }, [open, selectedYear, token]);

  const options = useMemo(
    () =>
      (students || [])
        .map((s) => ({
          id: s.id ?? s.studentId,
          label: `${s.studentName || ""} ${s.studentSurname || ""}`.trim() || `#${s.id ?? s.studentId}`,
        }))
        .filter((o) => o.id != null && String(o.id) !== String(destStudentId))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [students, destStudentId]
  );

  const pickSource = async (opt) => {
    setSource(opt);
    setSourceStays([]);
    setSourceBlocks([]);
    setPicked(new Set());
    if (!opt) return;
    setLoadingSource(true);
    try {
      const [stayRes, bkRes] = await Promise.all([
        fetch(`${SERVER_URL}api/student/${opt.id}/stays`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => (r.status === 204 ? [] : r.ok ? r.json() : []))
          .catch(() => []),
        fetch(`${SERVER_URL}api/student/${opt.id}/booking`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => (r.status === 204 ? [] : r.ok ? r.json() : []))
          .catch(() => []),
      ]);
      const stays = (Array.isArray(stayRes) ? stayRes : stayRes ? [stayRes] : [])
        .map(normStay)
        .filter((s) => s.start && s.end && inYear(selectedYear, s.start, s.end));
      setSourceStays(stays);
      setPicked(new Set(stays.map((s) => s.key))); // default: all
      setSourceBlocks((Array.isArray(bkRes) ? bkRes : []).map(toBlock).filter((b) => b.start && b.end));
    } catch (e) {
      setSourceStays([]);
      setSourceBlocks([]);
    } finally {
      setLoadingSource(false);
    }
  };

  const toggle = (key) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // For each selected source stay: create it on the destination if there's no matching
  // stay (same campus + overlapping dates), and copy that stay's bookings across.
  const plan = useMemo(() => {
    if (!source) return null;
    const selected = sourceStays.filter((s) => picked.has(s.key));
    const createStays = [];
    const toAdd = [];
    const toAdjust = [];
    const toRemove = [];
    const removedIds = new Set();

    selected.forEach((S) => {
      const match = destStays.find(
        (d) => sameCampus(d.campus, S.campus) && overlaps(d.start, d.end, S.start, S.end)
      );
      if (!match) createStays.push(S);

      const clamp = (b) => {
        const cs = maxStr(b.start, S.start);
        const ce = minStr(b.end, S.end);
        return { customStart: cs, customEnd: ce, ok: cs <= ce };
      };
      const srcIn = sourceBlocks.filter(
        (b) => overlaps(b.start, b.end, S.start, S.end) && sameCampus(b.campus, S.campus)
      );
      const dstIn = destBlocks.filter(
        (b) => overlaps(b.start, b.end, S.start, S.end) && sameCampus(b.campus, S.campus)
      );
      const dstByProduct = new Map();
      dstIn.forEach((b) => {
        if (!dstByProduct.has(b.productId)) dstByProduct.set(b.productId, b);
      });
      const srcProductIds = new Set(srcIn.map((b) => b.productId));

      srcIn.forEach((b) => {
        const m = dstByProduct.get(b.productId);
        const c = clamp(b);
        if (!m) {
          toAdd.push({ src: b, ...c });
        } else {
          const differs =
            m.start !== c.customStart ||
            m.end !== c.customEnd ||
            Number(m.actualCharge) !== Number(b.actualCharge);
          if (differs) toAdjust.push({ src: b, dest: m, ...c });
        }
      });
      dstIn.forEach((b) => {
        if (!srcProductIds.has(b.productId) && !removedIds.has(b.id)) {
          toRemove.push(b);
          removedIds.add(b.id);
        }
      });
    });

    return { createStays, toAdd, toAdjust, toRemove };
  }, [source, sourceStays, sourceBlocks, picked, destStays, destBlocks]);

  const nothingToDo =
    plan &&
    !plan.createStays.length &&
    !plan.toAdd.length &&
    !plan.toAdjust.length &&
    !plan.toRemove.length;

  const apply = async () => {
    if (!plan || nothingToDo) return;
    setApplying(true);
    const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
    try {
      for (const S of plan.createStays) {
        const res = await fetch(`${SERVER_URL}api/student/${destStudentId}/stay`, {
          method: "POST",
          headers: h,
          body: JSON.stringify({ campus: S.campus, arrivalDate: S.start, departureDate: S.end }),
        });
        if (!res.ok) throw new Error(`create stay ${S.campus} → ${res.status}`);
      }
      for (const a of plan.toAdd) {
        if (!a.ok) continue;
        const res = await fetch(
          `${SERVER_URL}api/bookings/studentId/${destStudentId}/productId/${a.src.productId}`,
          {
            method: "POST",
            headers: h,
            body: JSON.stringify({
              actualCharge: Number(a.src.actualCharge) || 0,
              bookingStatus: a.src.raw?.bookingStatus || "PROVISIONAL",
              customStart: a.customStart,
              customEnd: a.customEnd,
            }),
          }
        );
        if (!res.ok) throw new Error(`add ${a.src.productName} → ${res.status}`);
      }
      for (const a of plan.toAdjust) {
        const res = await fetch(`${SERVER_URL}api/bookings/${a.dest.id}`, {
          method: "PUT",
          headers: h,
          body: JSON.stringify({
            ...a.dest.raw,
            actualCharge: Number(a.src.actualCharge) || 0,
            customStart: a.customStart,
            customEnd: a.customEnd,
          }),
        });
        if (!res.ok) throw new Error(`adjust ${a.src.productName} → ${res.status}`);
      }
      for (const b of plan.toRemove) {
        const res = await fetch(`${SERVER_URL}api/bookings/${b.id}/product/${b.productId}`, {
          method: "DELETE",
          headers: h,
        });
        if (!res.ok) throw new Error(`remove ${b.productName} → ${res.status}`);
      }
      await onApplied?.();
      onClose?.();
    } catch (e) {
      window.alert(`Copy failed: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy bookings from another student</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Pick a student and which of their stays to duplicate. Each stay is created on this
            student if they don&apos;t already have a matching one, and its bookings are copied
            across (adding, adjusting and removing so they match).
          </Typography>

          <Autocomplete
            options={options}
            value={source}
            onChange={(_, v) => pickSource(v)}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => <TextField {...params} label="Copy from" size="small" />}
          />

          {loadingSource ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2">Loading their stays &amp; bookings…</Typography>
            </Stack>
          ) : source ? (
            sourceStays.length === 0 ? (
              <Alert severity="warning">That student has no stays in {selectedYear}.</Alert>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2">Stays to duplicate</Typography>
                  {sourceStays.map((s) => (
                    <FormControlLabel
                      key={s.key}
                      control={<Checkbox size="small" checked={picked.has(s.key)} onChange={() => toggle(s.key)} />}
                      label={
                        <Typography variant="body2">
                          {s.campus || "—"} · {s.start} → {s.end}
                        </Typography>
                      }
                    />
                  ))}
                </Box>

                {plan &&
                  (nothingToDo ? (
                    <Alert severity="success">Already in sync — nothing to change.</Alert>
                  ) : (
                    <Box>
                      {plan.createStays.length > 0 && (
                        <>
                          <Typography variant="subtitle2" color="primary.main">
                            Create stay
                          </Typography>
                          {plan.createStays.map((s, i) => (
                            <Typography key={`cs-${i}`} variant="body2">
                              ＋ {s.campus} · {s.start} → {s.end}
                            </Typography>
                          ))}
                        </>
                      )}
                      {plan.toAdd.length > 0 && (
                        <>
                          <Typography variant="subtitle2" color="success.main" sx={{ mt: 1 }}>
                            Add
                          </Typography>
                          {plan.toAdd.map((a, i) => (
                            <Typography key={`add-${i}`} variant="body2">
                              + {a.src.productName} ({a.customStart} → {a.customEnd})
                              {a.ok ? "" : " — skipped"}
                            </Typography>
                          ))}
                        </>
                      )}
                      {plan.toAdjust.length > 0 && (
                        <>
                          <Typography variant="subtitle2" color="warning.main" sx={{ mt: 1 }}>
                            Adjust
                          </Typography>
                          {plan.toAdjust.map((a, i) => (
                            <Typography key={`adj-${i}`} variant="body2">
                              ~ {a.src.productName} → ({a.customStart} → {a.customEnd}
                              {can("finance") ? `, £${Number(a.src.actualCharge) || 0}` : ""})
                            </Typography>
                          ))}
                        </>
                      )}
                      {plan.toRemove.length > 0 && (
                        <>
                          <Typography variant="subtitle2" color="error.main" sx={{ mt: 1 }}>
                            Remove
                          </Typography>
                          {plan.toRemove.map((b, i) => (
                            <Typography key={`rem-${i}`} variant="body2">
                              − {b.productName} ({b.start} → {b.end})
                            </Typography>
                          ))}
                        </>
                      )}
                    </Box>
                  ))}
              </>
            )
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          Cancel
        </Button>
        <Button variant="contained" onClick={apply} disabled={!plan || nothingToDo || applying}>
          {applying ? "Applying…" : "Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
